import { create } from 'zustand';
import { useAppStore } from '../stores/app';
import * as deviceDetect from 'react-device-detect';
import { RoomEvent } from 'livekit-client';

/**
 * Second Camera Store - Manages second camera device connection, video streaming, and RPC communication
 * 
 * This Zustand store handles the complete lifecycle of second camera functionality in the proctoring platform:
 * - Device enumeration and selection (preferring back-facing cameras)
 * - Video stream publishing and management via LiveKit
 * - RPC method registration for remote camera control
 * - Device data collection and broadcasting
 * - Session lifecycle management and cleanup
 * 
 * @typedef {Object} SecondCameraState
 * @property {boolean} initialized - Whether the store has been fully initialized
 * @property {boolean} isConnected - Current connection status to the media server
 * @property {MediaDeviceInfo[]} videoDevices - Array of available video input devices
 * @property {string|null} currentDeviceId - ID of the currently selected video device
 * @property {string|null} currentDeviceLabel - Human-readable label of current device
 * @property {MediaStream|null} videoStream - Active video stream from the selected device
 * @property {Object|null} mediaAgent - LiveKit media agent instance for room communication
 * @property {boolean} isPublishingStream - Whether video stream is currently being published
 * @property {boolean} isReplacingStream - Whether camera is being switched (loading state)
 * @property {Object} deviceData - Collected device information (OS, browser, connection)
 * @property {boolean} hasSessionEnded - Whether the proctoring session has ended
 */
const useSecondCameraStore = create((set, get) => ({
    // State Properties
    /** @type {boolean} Whether the store has been fully initialized with devices and media agent */
    initialized: false,

    /** @type {boolean} Current connection status to the media server */
    isConnected: false,

    /** @type {MediaDeviceInfo[]} Array of available video input devices */
    videoDevices: [],

    /** @type {string|null} ID of the currently selected video device */
    currentDeviceId: null,

    /** @type {string|null} Human-readable label of current device */
    currentDeviceLabel: null,

    /** @type {MediaStream|null} Active video stream from the selected device */
    videoStream: null,

    /** @type {Object|null} LiveKit media agent instance for room communication */
    mediaAgent: null,

    /** @type {boolean} Whether video stream is currently being published */
    isPublishingStream: false,

    /** @type {boolean} Whether camera is being switched (loading state) */
    isReplacingStream: false,

    /** @type {Object} Collected device information (OS, browser, connection) */
    deviceData: {},

    /** @type {boolean} Whether the proctoring session has ended */
    hasSessionEnded: false,

    /**
     * Initialize the second camera store with device enumeration, media agent setup, and RPC registration
     * 
     * This method performs the complete initialization sequence:
     * 1. Enumerates available video devices and selects optimal camera
     * 2. Sets up LiveKit media agent with room event listeners
     * 3. Registers RPC methods for remote camera control
     * 4. Publishes initial video stream
     * 5. Collects and broadcasts device information
     * 
     * @param {Object} options - Configuration options for initialization
     * @returns {Promise<{mediaAgent: Object}>} The initialized media agent instance
     */
    initialize: async (options = {}) => {
        // Set defaults for options (currently no default options defined)
        const defaultOptions = {
        };

        // Merge provided options with defaults
        options = { ...defaultOptions, ...options };

        // Enumerate and select video devices, preferring back-facing cameras
        await get().getVideoDevices();

        // Get the shared media agent instance from the app store
        const mediaAgent = await useAppStore.getState().getMediaAgent();

        // Setup room event listeners for session lifecycle management
        mediaAgent.getRoom().on(RoomEvent.Disconnected, async (data) => {
            // Check session status when disconnected to determine if session ended
            const sessionData = await useAppStore.getState().getApiClient().getSession();

            // If session is closed or cancelled, clean up resources
            if (['closed', 'cancelled'].includes(sessionData?.status?.toLowerCase())) {
                // Update UI state to reflect session end
                set({ hasSessionEnded: true });

                // Stop all video tracks to free camera resources
                get().videoStream.getTracks().forEach((track) => track.stop());

                // Clear the video stream reference
                set({ videoStream: null });
            }
        });

        // Store media agent for future RPC and streaming operations
        set({ mediaAgent });

        // Register RPC methods for remote camera control
        await get().registerRPCMethods();

        // Immediately publish the selected video stream
        await get().publishStream();

        // Collect comprehensive device information for diagnostics and compatibility
        const deviceData = {
            connection: {
                effectiveType: navigator.connection?.effectiveType, // Network connection type (4g, wifi, etc.)
                type: navigator.connection?.type,                   // Connection technology
                downlink: navigator.connection?.downlink,           // Effective bandwidth estimate
                downlinkMax: navigator.connection?.downlinkMax,     // Maximum downlink speed
            },
            device: {
                os: deviceDetect.osName,           // Operating system name
                osVersion: deviceDetect.osVersion, // OS version
                type: deviceDetect.deviceType,     // Device type (mobile, tablet, desktop)
                platform: deviceDetect.platform,  // Platform identifier
                vendor: deviceDetect.mobileVendor, // Mobile device vendor
            },
            browser: {
                name: deviceDetect.browserName,    // Browser name
                version: deviceDetect.browserVersion, // Browser version               
            }
        };

        // Broadcast device information to all participants for monitoring
        await get().broadcastEvent('secondCameraInitialized', deviceData);

        // Mark initialization complete and store device data
        set({ initialized: true, deviceData });
        return { mediaAgent };
    },

    /**
     * Enumerate available video devices and select the optimal camera for second camera functionality
     * 
     * This method handles the complete device selection process:
     * 1. Requests camera permissions to ensure device labels are available
     * 2. Enumerates all video input devices
     * 3. Prioritizes back-facing cameras for better document/environment capture
     * 4. Creates and manages video streams with the selected device
     * 5. Updates store state with device information and active stream
     * 
     * @returns {Promise<void>} Resolves when device enumeration and selection is complete
     */
    getVideoDevices: async () => {
        let cameras = [];
        let videoDeviceId = null;
        let videoDeviceLabel = null;
        let videoStream = null;

        try {
            console.log('=== getVideoDevices() called ===');

            // Detect browser for Chrome-specific handling
            const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
            console.log('Browser detection:', { isChrome, userAgent: navigator.userAgent });

            // Request camera permissions first to ensure device labels are available
            // Without permissions, enumerateDevices() returns generic labels like "Camera 1"
            // Use portrait orientation (height > width) for mobile devices
            const videoConstraints = isChrome ? {
                width: { ideal: 1080 },   // Portrait: width is smaller
                height: { ideal: 1920 },  // Portrait: height is larger
                facingMode: 'environment' // Prefer back camera
            } : true;

            console.log('Requesting initial camera access with constraints:', videoConstraints);
            const defaultStream = await navigator.mediaDevices.getUserMedia({
                video: true,
            });
            console.log('Initial stream obtained:', defaultStream);

            // Enumerate all available media devices and filter for video inputs
            const devices = await navigator.mediaDevices.enumerateDevices();
            cameras = devices.filter((device) => device.kind === 'videoinput');

            // Store the available video devices in state for UI display
            set({ videoDevices: cameras });

            // Prioritize back-facing cameras as they're better for document scanning and environment capture
            // Look for common keywords that indicate rear-facing cameras
            const backCamera = cameras.find(camera =>
                camera.label.toLowerCase().includes('back') ||
                camera.label.toLowerCase().includes('rear') ||
                camera.label.toLowerCase().includes('environment'));

            // Use back camera if found, otherwise fall back to the default camera
            videoDeviceId = backCamera ? backCamera.deviceId : defaultStream
                .getVideoTracks()[0]
                .getSettings().deviceId;

            if (videoDeviceId) {
                // Find the selected camera in our device list to get its label
                const selectedCamera = cameras.find(
                    (cam) => cam.deviceId === videoDeviceId
                );

                // Set a human-readable label for the selected camera
                videoDeviceLabel = selectedCamera ? selectedCamera.label : 'Unknown Camera';

                // If we selected a different camera than the default, we need to create a new stream
                if (backCamera && videoDeviceId !== defaultStream.getVideoTracks()[0].getSettings().deviceId) {
                    // Stop the default stream to free resources
                    defaultStream.getTracks().forEach((track) => track.stop());

                    // Create a new stream with the specifically selected camera
                    // Use explicit constraints for Chrome mobile compatibility
                    const newStreamConstraints = {
                        deviceId: { exact: videoDeviceId }
                    };

                    console.log('Creating new stream for back camera with constraints:', newStreamConstraints);
                    videoStream = await navigator.mediaDevices.getUserMedia({
                        video: newStreamConstraints,
                        audio: false, // Second camera doesn't need audio
                    });
                    console.log('New stream created:', videoStream);

                    // Log track settings to verify dimensions
                    const track = videoStream.getVideoTracks()[0];
                    console.log('New stream track settings:', track.getSettings());
                } else {
                    // Use the existing default stream if it's already the optimal camera
                    console.log('Using default stream');
                    videoStream = defaultStream;
                    const track = videoStream.getVideoTracks()[0];
                    console.log('Default stream track settings:', track.getSettings());
                }

                // TODO: Add event annotation logging for device enumeration and selection

                // Update store state with the selected device and active stream
                set({
                    videoDevices: cameras,
                    videoStream,
                    currentDeviceId: videoDeviceId,
                    currentDeviceLabel: videoDeviceLabel,
                });

            } else {
                // Fallback case: no suitable device found
                console.log("Default device not found, stop tracks...");
                // TODO: Implement proper error handling - should we show an error UI?
                defaultStream.getTracks().forEach((track) => track.stop());
            }
        } catch (error) {
            // Fallback: open in new tab if permissions are denied or other errors occur
            window.open(window.location.href, '_blank');
            // TODO: Implement proper error handling and user feedback
            console.error('Error initializing video devices:', error);
        }
    },

    /**
     * Publish the current video stream to the LiveKit room as a second camera track
     * 
     * This method publishes the selected video stream to other participants in the room.
     * The track is published with the name 'secondCamera' to distinguish it from primary camera feeds.
     * 
     * @returns {Promise<void>} Resolves when stream publishing is complete
     */
    publishStream: async () => {
        try {
            console.log('=== publishStream() called ===');

            // Get browser info for debugging
            const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
            const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
            console.log('Browser:', { isChrome, isSafari, userAgent: navigator.userAgent });

            // Get the video stream
            const videoStream = get().videoStream;
            console.log('videoStream:', videoStream);
            console.log('videoStream active:', videoStream?.active);
            let track;
            if (videoStream) {
                const videoTracks = videoStream.getVideoTracks();
                console.log('videoTracks:', videoTracks);

                if (videoTracks.length > 0) {
                    track = videoTracks[0];
                    const settings = track.getSettings();

                    console.log('Video track details:', {
                        id: track.id,
                        label: track.label,
                        enabled: track.enabled,
                        muted: track.muted,
                        readyState: track.readyState,
                        settings: settings
                    });

                    // Chrome mobile sometimes needs explicit constraints
                    if (isChrome && (!settings.width || !settings.height)) {
                        console.warn('Chrome: Video track has no dimensions, may need to recreate stream');
                    }
                }
            }

            // Publish the video track using the media agent with current device label and track name
            console.log('Calling publishVideoTrack with:', {
                deviceLabel: get().currentDeviceLabel,
                trackName: 'secondCamera'
            });



            // Configure encoding options for Chrome mobile
            // Chrome needs explicit encoding parameters to properly transmit video
            const publishOptions = {
                name: 'secondCamera',
                // Source type - explicitly mark as camera
                source: 'camera',

                // Video encoding settings
                videoEncoding: {
                    maxBitrate: 2500000,      // 2.5 Mbps max bitrate
                    maxFramerate: 15,          // 30 fps
                },
                // Simulcast configuration for adaptive quality
                // For second camera, use limited simulcast layers for mobile bandwidth
                simulcast: true,
                simulcastLayers: [
                    // High quality layer (original resolution)
                    {
                        rid: 'h',
                        maxBitrate: 2500000,
                        maxFramerate: 15,
                        scaleResolutionDownBy: 1.0
                    },
                    // Medium quality layer (half resolution)
                    {
                        rid: 'm',
                        maxBitrate: 1000000,
                        maxFramerate: 10,
                        scaleResolutionDownBy: 2.0
                    },
                    // Low quality layer (quarter resolution)
                    {
                        rid: 'l',
                        maxBitrate: 300000,
                        maxFramerate: 5,
                        scaleResolutionDownBy: 4.0
                    }
                ],
                // Video codec preference with backup - H.264 preferred for mobile hardware encoding
                videoCodec: 'h264',
                // Backup codecs if H.264 fails (VP8 is widely supported, VP9 as fallback)
                backupCodec: {
                    codec: 'vp8',
                    encoding: {
                        maxBitrate: 2500000,
                        maxFramerate: 15,
                    }
                },
                // Scalability mode - single layer for mobile
                scalabilityMode: 'L1T1',
                // Degradation preference - maintain framerate over resolution for smoother video
                degradationPreference: 'maintain-framerate',
                // Stop mic track on mute (mobile battery optimization)
                stopMicTrackOnMute: true,
                // DTX (discontinuous transmission) for bandwidth optimization
                dtx: true,
                // RED (redundant encoding) for packet loss recovery
                red: false,
                // Force software encoding if hardware fails
                forceSWCodec: false
            };
            /*
                        console.log('Publishing track with encoding options:', publishOptions);
                        console.log('Track to publish:', track);
                        console.log('Track settings:', track.getSettings());
                        console.log('Track readyState:', track.readyState);
                        console.log('Track enabled:', track.enabled);
                        console.log('Track muted:', track.muted);
            */
            //const results = await get().mediaAgent.getLocalParticipant().publishTrack(track, publishOptions);
            //console.log('publishVideoTrack result:', results);
            //const success = true;
            //const success = await get().mediaAgent?.publishVideoTrack(get().currentDeviceLabel, 'secondCamera');
            console.log('Track to publish:', track);
            const success = await get().mediaAgent?.publishVideoTrack(track, 'secondCamera', publishOptions);


            // Handle case where user cancels permission or publishing fails
            if (!success) {
                console.error('Failed to publish video track');
                // TODO: Implement proper error handling instead of page reload
                //window.location.reload();
            } else {
                // CHECK WHAT WAS ACTUALLY PUBLISHED
                const room = get().mediaAgent?.getRoom();
                const localParticipant = room?.localParticipant;
                const publications = Array.from(localParticipant?.videoTrackPublications?.values() || []);
                console.log('All video track publications after publish:', publications.map(pub => ({
                    trackName: pub.trackName,
                    trackSid: pub.trackSid,
                    muted: pub.isMuted,
                    track: pub.track,
                    trackSettings: pub.track?.mediaStreamTrack?.getSettings()
                })));
            }

            // Update state to reflect that stream is now being published
            set({ isPublishingStream: true });
        } catch (error) {
            console.error('Error in publishStream:', error);
            alert(error.message);
            // Fallback: open in new tab if publishing fails
            window.open(window.location.href, '_blank');
        }
    },

    /**
     * Change the active camera device and update the published video stream
     * 
     * This method handles switching between available cameras while maintaining the active stream.
     * It updates the LiveKit room's active device and broadcasts the change to all participants.
     * 
     * @param {Object} params - Camera change parameters
     * @param {string} params.label - Human-readable label of the target camera device
     * @param {string} params.identity - Identity of the participant requesting the change
     * @returns {Promise<Object|undefined>} Device information if successful, undefined if failed
     */
    changeCamera: async ({ label, identity }) => {
        try {
            // Set loading state to show UI feedback during camera switch
            set({ isReplacingStream: true });

            // Find the target device in our enumerated device list
            const selectedDevice = get().videoDevices.find((device) => device.label === label);

            if (selectedDevice) {
                // Get the current second camera track publication that we'll be replacing
                const publication = get().mediaAgent?.getRoom().localParticipant?.getTrackPublicationByName('secondCamera');

                if (publication) {
                    // Switch the active video input device in the LiveKit room
                    await get().mediaAgent?.getRoom().switchActiveDevice('videoinput', selectedDevice.deviceId, true);

                    // Prepare device change notification for all participants
                    // Use data channel instead of RPC to broadcast to all participants simultaneously
                    const dataChannelPayload = {
                        deviceLabel: label,
                        deviceId: selectedDevice.deviceId,
                    };

                    // Broadcast the camera change event to all participants
                    await get().broadcastEvent('secondCameraDeviceChange', dataChannelPayload);

                    // Update local store state with new device information
                    set({
                        currentDeviceId: selectedDevice.deviceId,
                        currentDeviceLabel: selectedDevice.label,
                        isPublishingStream: true,
                        isReplacingStream: false, // Clear loading state
                    });

                    return dataChannelPayload;
                } else {
                    // No active publication found - cannot change camera
                    set({ isReplacingStream: false });
                }
            } else {
                // Target device not found in available devices
                set({ isReplacingStream: false });
            }

            // TODO: Create event annotation logging who changed the camera

        } catch (error) {
            // Handle any errors during camera change process
            set({ isReplacingStream: false });
        }
    },

    /**
     * Register RPC (Remote Procedure Call) methods for remote camera control
     * 
     * This method sets up RPC endpoints that allow other participants (like proctors)
     * to remotely control the second camera device. Available RPC methods:
     * - getCameraList: Returns available camera devices
     * - getDeviceData: Returns device information (OS, browser, connection)
     * - changeCamera: Switches to a different camera device
     * - unpublishVideo: Stops video publishing (placeholder implementation)
     * 
     * @returns {Promise<void>} Resolves when all RPC methods are registered
     */
    registerRPCMethods: async () => {
        try {
            // Register RPC method to get list of available cameras
            get().mediaAgent.getRoom().localParticipant?.registerRpcMethod(
                'getCameraList', (data) => {
                    return get().__rpc__getCameraList(data);
                },
            );

            // Register RPC method to get device information (OS, browser, connection)
            get().mediaAgent.getRoom().localParticipant?.registerRpcMethod(
                'getDeviceData', (data) => {
                    return get().__rpc__getDeviceData(data);
                },
            );

            // Register RPC method to remotely change camera device
            get().mediaAgent.getRoom().localParticipant?.registerRpcMethod(
                'changeCamera',
                async (data) => {
                    try {
                        // Parse the RPC payload to extract device label
                        const payload = JSON.parse(data.payload);
                        const label = payload?.deviceLabel;

                        if (label) {
                            try {
                                // Execute the camera change and return success response
                                const response = await get().changeCamera({ label: label, identity: data.callerIdentity });

                                return JSON.stringify({
                                    successful: true,
                                    data: {
                                        deviceLabel: response.deviceLabel,
                                        deviceId: response.deviceId,
                                    }
                                });
                            } catch (error) {
                                return JSON.stringify({ successful: false, error: error });
                            }
                        } else {
                            // Return error if no device label provided
                            return JSON.stringify({ successful: false, error: 'No device label provided' });
                        }
                    } catch (error) {
                        return JSON.stringify({ successful: false, error: error });
                    }
                },
            );

            // Register RPC method to unpublish video (placeholder implementation)
            get().mediaAgent.getRoom().localParticipant?.registerRpcMethod(
                'unpublishVideo',
                async (data) => {
                    // TODO: Implement proper unpublish video functionality
                    const videoDevices = await getVideoDevices();
                    return JSON.stringify(videoDevices);
                },
            );

        } catch (error) {
            // Handle RPC registration errors
            console.error('Error registering RPC methods:', error);
        }
    },

    /**
     * Broadcast an event with data to all participants in the room via data channel
     * 
     * This method sends custom events to all participants using LiveKit's data channel.
     * Events are sent reliably to ensure all participants receive the information.
     * 
     * @param {string} event - The event type/name to broadcast
     * @param {Object} data - The data payload to send with the event
     * @returns {Promise<void>} Resolves when the event is broadcast
     */
    broadcastEvent: async (event, data) => {
        try {
            // Create standardized event payload with event type and data
            const dataChannelPayload = {
                spEvent: event,  // SmarterServices Platform event identifier
                data: data,      // Event-specific data payload
            };

            // Publish the event data to all participants via reliable data channel
            get().mediaAgent.getRoom().localParticipant?.publishData(
                new TextEncoder().encode(JSON.stringify(dataChannelPayload)),
                { reliable: true }  // Ensure reliable delivery
            );
        } catch (error) {
            console.log(error);
        }
    },

    // RPC Method Handlers - Internal methods that handle remote procedure calls

    /**
     * RPC Handler: Get list of available camera devices
     * 
     * Returns the current list of video input devices along with the currently selected device.
     * This allows remote participants to see what cameras are available for switching.
     * 
     * @returns {string} JSON stringified response with device list and current selection
     */
    __rpc__getCameraList: async () => {
        const response = { successful: true, data: {} };

        try {
            // Format devices as simplified objects with id and label for remote consumption
            response.data.deviceList = get().videoDevices.map((device) => {
                return {
                    id: device.deviceId,    // Unique device identifier
                    label: device.label     // Human-readable device name
                };
            });

            // Include current device selection for context
            response.data.currentDeviceId = get().currentDeviceId;
            response.data.currentDeviceLabel = get().currentDeviceLabel;

        } catch (error) {
            // Return error response if device enumeration fails
            response.successful = false;
            response.error = error;
        }

        return JSON.stringify(response);
    },

    /**
     * RPC Handler: Get comprehensive device information
     * 
     * Returns collected device data including OS, browser, and connection information.
     * This data is used for compatibility checking and diagnostics by remote participants.
     * 
     * @returns {string} JSON stringified response with complete device information
     */
    __rpc__getDeviceData: async () => {
        const response = { successful: true, data: {} };

        try {
            // Return the complete device data object collected during initialization
            response.data = get().deviceData;
        } catch (error) {
            // Return error response if device data is unavailable
            response.successful = false;
            response.error = error;
        }

        return JSON.stringify(response);
    },

    /**
     * RPC Handler: Change camera device (placeholder)
     * 
     * This method is currently not implemented as camera changes are handled
     * directly by the changeCamera method via the 'changeCamera' RPC endpoint.
     * 
     * @returns {Promise<void>} Currently does nothing
     */
    __rpc__changeCamera: async () => {
        // TODO: Implement if direct camera change handling is needed
        // Currently handled by the 'changeCamera' RPC method registration
    },

    /**
     * RPC Handler: Unpublish video stream (placeholder)
     * 
     * This method would handle stopping the video stream publication.
     * Currently not implemented but reserved for future functionality.
     * 
     * @returns {Promise<void>} Currently does nothing
     */
    __rpc__unpublishVideo: async () => {
        // TODO: Implement video stream unpublishing functionality
        // Should stop current video track and update state accordingly
    },

}));

export default useSecondCameraStore;
export { useSecondCameraStore };
